<template>
  <vue-final-modal
    :model-value="dialog"
    :hide-overlay="false"
    :click-to-close="true"
    :esc-to-close="true"
    classes="modal-container"
    content-class="modal-content"
    @update:model-value="val => emit('update:dialog', val)"
  >
    <div class="modal-header justify-content-between">
      <span class="modal__title text-white">Assign Training</span>
      <div>
        <button type="button" class="btn-close btn-close-white" aria-label="Close" @click="closeDialog"></button>
      </div>
    </div>

    <div class="modal__body">
      <div class="modal__content mt-3 mb-4">
        <div v-if="loading" class="text-center p-5">Loading users...</div>
        <div v-else class="px-3">
          <multiselect
            v-model="selectedUsers"
            :options="optionsWithAll"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            placeholder="Search or select users"
            track-by="id"
            :custom-label="customLabel"
          >
            <template #noResult><span class="text-muted">No users found.</span></template>
          </multiselect>
        </div>
      </div>
    </div>
    <div class="modal__action d-flex justify-content-center p-2">
      <button type="button" class="btn btn-primary mb-4" @click="assignTraining" :disabled="selectedUsers.length === 0 || assigning">
        <span v-if="assigning" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span v-if="!assigning">Assign</span>
      </button>
    </div>
  </vue-final-modal>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, computed, watch } from 'vue'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

/* global defineProps, defineEmits */

const { proxy } = getCurrentInstance()

const props = defineProps({
  dialog: {
    type: Boolean,
    required: true
  },
  itemId: {
    type: [Number, String],
    required: true
  },
  itemType: {
    type: String,
    required: true,
    validator: (value) => ['video', 'program'].includes(value)
  }
})

const emit = defineEmits(['update:dialog'])

const users = ref([])
const loading = ref(false)
const selectedUsers = ref([])
const assigning = ref(false)

const orgID = ref(proxy.$helper.setOrganisationID())

const allOption = { id: 'all', first_name: 'All Users', last_name: '' }

const optionsWithAll = computed(() => [allOption, ...users.value])

watch(selectedUsers, (newValue, oldValue) => {
  const allIsSelectedInNew = newValue.some(u => u.id === 'all')
  const allWasSelectedInOld = oldValue.some(u => u.id === 'all')

  // Case 1: "All" is newly selected.
  // This clears other selections and selects only "All".
  if (allIsSelectedInNew && !allWasSelectedInOld) {
    selectedUsers.value = [allOption]
    return
  }

  // Case 2: A user is selected while "All" is already selected.
  // This deselects "All" and keeps the other user(s).
  if (allIsSelectedInNew && newValue.length > 1) {
    selectedUsers.value = newValue.filter(u => u.id !== 'all')
  }
})

const fetchUsers = async () => {
  loading.value = true
  try {
    const response = await proxy.$axios.get('/users/organisations/' + orgID.value)
    if (response.data.success) {
      // Sort users by first_name in ascending order
      users.value = response.data.users.sort((a, b) => {
        const nameA = a.first_name.toLowerCase()
        const nameB = b.first_name.toLowerCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })
    } else {
      proxy.$toast.error(response.data.message)
    }
  } catch (error) {
    console.error("Error fetching users:", error)
  }
  loading.value = false
}

const assignTraining = async () => {
  assigning.value = true;
  try {
    const isAllSelected = selectedUsers.value.some(user => user.id === 'all');
    const payload = {
      user_ids: isAllSelected
        ? users.value.map(user => user.id) // Send all user IDs if 'All' is selected
        : selectedUsers.value.map(user => user.id)
    };

    let url = '';
    if (props.itemType === 'video') {
      url = `/orgadmin/trainings/organisations/${orgID.value}/videos/${props.itemId}/assign-next`;
    } else if (props.itemType === 'program') {
      url = `/orgadmin/trainings/organisations/${orgID.value}/programs/${props.itemId}/assign-to-users`;
    } else {
      proxy.$toast.error('Invalid training type.');
      assigning.value = false;
      return;
    }

    const response = await proxy.$axios.post(url, payload);

    if (response.data.success) {
      proxy.$toast.success(response.data.message);
      closeDialog();
    } else {
      proxy.$toast.error(response.data.message);
    }
  } catch (error) {
    proxy.$toast.error("An error occurred while re-assigning the training.");
    console.error("Error re-assigning training:", error);
  } finally {
    assigning.value = false;
  }
}

const customLabel = ({ first_name, last_name }) => {
  return `${first_name} ${last_name}`
}

onMounted(() => {
  fetchUsers()
})

function closeDialog() {
  emit('update:dialog', false)
}
</script>

<style scoped>
/*
  Override default modal content styles to allow the vue-multiselect dropdown
  to display over the modal boundaries.
*/
:deep(.modal__content) {
  overflow: visible;
  height: 100%;
}

:deep(.modal__body) {
  overflow: visible;
  height: 100%;
}
</style>
